// ============================================================================
// documentoEstado.ts — FONTE ÚNICA DE VERDADE do estado de um documento.
// ----------------------------------------------------------------------------
// Toda tela (Home, Arquivos, e qualquer outra) resolve "assinado / não assinado /
// contestado / selo / obrigatório assinar" AQUI, pelas MESMAS regras e os MESMOS
// dados locais. NENHUMA tela deve reler `d.assinado` / `d.status_contestacao` crus
// nem recombinar essas regras — sempre consumir o DocEstado deste módulo. Isso
// garante que o mesmo documento tenha o MESMO resultado em qualquer área.
//
// Verdades (não confiar no campo `assinado`/`status_contestacao` cru, que OSCILA
// por cache 15s/réplicas do backend):
//  • ASSINADO no servidor (2 checks) = o arquivo assinado `_Ass.pdf` EXISTE.
//  • ASSINADO local pendente (1 check) = marca 'O' local OU SIGNATURE na fila, dentro
//    do grace de 3min após a entrega (senão foi revertido → volta a PENDENTE).
//  • CONTESTAÇÃO é STICKY: só os TERMINAIS do admin ('A' aceita / 'R' revogada)
//    limpam a marca local; 'S'(em contestação) e '' do servidor OSCILAM e NÃO limpam.
//  • Identidade SEMPRE por id_adm_contra_cheque_colaborador (o arquivo pode ser
//    substituído por um novo id — NUNCA casar por tipo+mês).
// ============================================================================

import {
  ehRevertConfirmado,
  lerLogAssinadosDoTenant,
  lerUserDataFetchedAt,
  type DocAssinadoEntry,
} from "@/services/docAssinadoLog";
import { SyncService } from "@/services/Sync";
import { documentoVisivelAoColaborador } from "@/utils/documentoVisivel";

/** Grace (ms) p/ o servidor gerar o _Ass.pdf após a assinatura ser entregue. */
export const DOC_GRACE_MS = 180000;

/** Identidade canônica do documento. NUNCA tipo+mês. Fallback d.id só p/ compat. */
export function docKey(doc: any): string {
  return String(doc?.id_adm_contra_cheque_colaborador ?? doc?.id ?? "");
}

// ── Gate de sessão COMPARTILHADO (anti-flicker do cold-open) ─────────────────
// true depois que o 1º /iniciar da SESSÃO do processo assentou (sucesso OU erro).
// A Home seta no .finally do seu 1º refresh; QUALQUER superfície que avalie
// obrigatorioAssinar (Home, BottomBar…) deve passar este valor ao resolver —
// senão o mesmo doc legado fica "pendente" numa superfície e "assinado" noutra.
let _sessaoDocsConfirmada = false;
export function marcarSessaoDocsConfirmada(): void {
  _sessaoDocsConfirmada = true;
}
export function sessaoDocsConfirmada(): boolean {
  return _sessaoDocsConfirmada;
}

/** Chave de dedup da fila de assinatura (1 assinatura por doc+página). */
export function sigKey(doc: any): string {
  return `${docKey(doc)}:${doc?.pagina ?? ""}`;
}

export type Selo = "2checks" | "1check" | "nenhum";
export type Contestacao = "" | "S" | "A" | "R";
export type StatusExibicao = "ASSINADO" | "PENDENTE";

export interface DocEstado {
  id: string;
  ativo: boolean; // ativo_inativo !== 'I'
  // O colaborador pode ver: ativo E fora da competência de teste (12/2222). É por este
  // campo que as telas filtram a lista — ver utils/documentoVisivel.ts.
  visivel: boolean;
  assinadoServidor: boolean; // _Ass.pdf existe (2 checks) — verdade estável
  assinadoLocalPendente: boolean; // marca 'O'/fila dentro do grace (1 check)
  assinado: boolean; // servidor || local pendente (exibição)
  selo: Selo;
  contestacao: Contestacao; // status_contestacao EFETIVO (sticky)
  contestado: boolean; // contestacao ∈ {'S','A'} (NÃO inclui 'R')
  obrigatorioAssinar: boolean; // !assinado && contestacao ∉ {'S','A'} ('R' e '' obrigam)
  statusExibicao: StatusExibicao;
  respostaContestacaoPendenteVista: boolean; // contestacao ∈ {A,R} e id ∉ vistas
  arquivoUrl: string; // nome_arquivo||url canônico (p/ download decidir revert)
}

/** Snapshot dos dados LOCAIS lido UMA vez; alimenta o resolver puro. */
export interface DocSnapshot {
  statusMap: Record<string, string>; // marca 'O'/'S' local
  deliveredMap: Record<string, number>; // carimbo de entrega (grace)
  contestacaoMap: Record<
    string,
    { motivo: string; data_log: string; terminal?: "A" | "R" }
  >;
  filaSignatureIds: Set<string>; // SIGNATURE na fila
  filaContestarIds: Set<string>; // DOCUMENTO_CONTESTAR na fila
  vistas: Set<string>; // respostas de contestação já vistas
  // Log PERSISTENTE de assinados neste aparelho (empresa ATUAL, projetado por docId)
  // + carimbo do último /iniciar REAL — juntos decidem o "revert por confirmação
  // fresca" (ver services/docAssinadoLog.ts). Sem timer cego, sem bool de rede.
  assinadosLog: Record<string, DocAssinadoEntry>;
  userDataFetchedAtMs: number;
}

/**
 * Carrega TODAS as fontes locais de estado de documento de uma só vez. As telas
 * chamam isto no foco/refresh e passam o snapshot ao resolver — garantindo que
 * qualquer tela leia exatamente o mesmo estado local ao trocar de tela.
 */
export async function carregarSnapshotLocalDocs(): Promise<DocSnapshot> {
  const [statusMap, deliveredMap, contestacaoMap, fila, vistas, assinadosLog, fetchedAtMs] =
    await Promise.all([
      SyncService.getLocalDocStatusMap().catch(() => ({}) as any),
      SyncService.getLocalDocDeliveredAtMap().catch(() => ({}) as any),
      SyncService.getLocalDocContestacaoMap().catch(() => ({}) as any),
      SyncService.getOfflineQueue().catch(() => [] as any[]),
      SyncService.getContestacoesVistas().catch(() => [] as any[]),
      lerLogAssinadosDoTenant().catch(() => ({}) as Record<string, DocAssinadoEntry>),
      lerUserDataFetchedAt().catch(() => 0),
    ]);

  const filaSignatureIds = new Set<string>();
  const filaContestarIds = new Set<string>();
  (fila || []).forEach((a: any) => {
    const id = a?.data?.id_adm_contra_cheque_colaborador;
    if (id == null || String(id).trim() === "") return; // nunca indexar chave vazia
    if (a?.type === "SIGNATURE") filaSignatureIds.add(String(id));
    else if (a?.type === "DOCUMENTO_CONTESTAR") filaContestarIds.add(String(id));
  });

  return {
    statusMap: statusMap || {},
    deliveredMap: deliveredMap || {},
    contestacaoMap: contestacaoMap || {},
    filaSignatureIds,
    filaContestarIds,
    vistas: new Set((vistas || []).map((v: any) => String(v))),
    assinadosLog: assinadosLog || {},
    userDataFetchedAtMs: fetchedAtMs || 0,
  };
}

/**
 * Resolve o estado canônico de UM documento. PURO (recebe nowMs; sem Date.now()
 * dentro) — testável e usável direto em useMemo de UI.
 */
export function resolverDoc(
  doc: any,
  snap: DocSnapshot,
  nowMs: number,
  sessaoConfirmada = true,
): DocEstado {
  const id = docKey(doc);
  const ativo =
    String(doc?.ativo_inativo ?? "A").trim().toUpperCase() !== "I";
  const visivel = documentoVisivelAoColaborador(doc);
  // URL canônica p/ download = preferir a URL COMPLETA (mesma ordem do Arquivos); o nome_arquivo
  // pode ser só o nome do arquivo (não baixável).
  const arquivoUrl = String(doc?.url || doc?.nome_arquivo || "");

  // ── Assinatura ── (ignora o campo `assinado` cru, que oscila). O sufixo _Ass.pdf pode estar no
  // nome_arquivo E/OU na url → checa os dois.
  const assinadoServidor =
    SyncService.isArquivoAssinadoServidor(doc?.nome_arquivo) ||
    SyncService.isArquivoAssinadoServidor(doc?.url);
  let assinadoLocalPendente = false;
  let confirmadoNoLog = false; // _Ass.pdf JÁ visto antes (oscilação de réplica) → selo 2 checks
  if (!assinadoServidor) {
    const temMarca = String(snap.statusMap[id] ?? "").toUpperCase() === "O";
    const naFila = snap.filaSignatureIds.has(id);
    const logEntry = snap.assinadosLog[id];
    if (logEntry) {
      // ── LOG PERSISTENTE (assinado NESTE aparelho) — revert só por CONFIRMAÇÃO FRESCA ──
      // O doc fica ASSINADO até que um /iniciar REAL (userDataFetchedAtMs), posterior à
      // ENTREGA + JANELA de 5min (e à CONFIRMAÇÃO + janela de oscilação, se o _Ass.pdf já
      // foi visto — o nome oscila entre réplicas com cache defasado), ainda venha SEM o
      // _Ass.pdf — aí sim é revert/falha real e volta a PENDENTE (estável: o carimbo só
      // anda pra frente). Propriedades: userData velho (remount Registro→Home) tem carimbo
      // velho → nunca cobra falso; OFFLINE o carimbo congela → nunca cobra falso; API
      // doente (500) não avança o carimbo; relógio absoluto errado é irrelevante (dois
      // carimbos do mesmo relógio). NÃO depende da marca 'O'/deliveredMap.
      assinadoLocalPendente = !ehRevertConfirmado(
        logEntry,
        naFila,
        snap.userDataFetchedAtMs,
      );
      // Já confirmado pelo servidor em fetch anterior → exibe 2 checks (não "regride"
      // para 1 check enquanto uma réplica defasada serve o nome antigo).
      confirmadoNoLog = assinadoLocalPendente && !!logEntry.confirmadoEmMs;
    } else if (temMarca || naFila) {
      // ── LEGADO (sem entrada no log — assinatura anterior a este deploy) ──
      const entregueMs = Number(snap.deliveredMap[id] || 0);
      const dentroGraca = entregueMs > 0 && nowMs - entregueMs <= DOC_GRACE_MS;
      const semCarimbo = entregueMs === 0;
      // Mantém 1 check se: ainda na fila (não entregue) OU dentro do grace OU sem
      // carimbo (ambíguo, marca recém-criada). Reverte só com carimbo expirado sem _Ass.pdf.
      // sessaoConfirmada=false (ex.: cold-open com userData ainda do CACHE, sem o _Ass.pdf, antes
      // do 1º /iniciar fresco da sessão): NÃO reverte 'O'→PENDENTE só porque o grace expirou — o
      // cache pode só estar defasado. Mantém 1 check até o servidor confirmar (evita o modal/card
      // de "assinatura pendente" PISCAR ao reabrir o app). Doc nunca-assinado (sem marca/fila) não
      // entra neste ramo → cobrança imediata, sem esperar confirmação.
      assinadoLocalPendente =
        naFila || dentroGraca || semCarimbo || !sessaoConfirmada;
    }
  }
  const assinado = assinadoServidor || assinadoLocalPendente;
  const selo: Selo =
    assinadoServidor || confirmadoNoLog
      ? "2checks"
      : assinadoLocalPendente
        ? "1check"
        : "nenhum";

  // ── Contestação (STICKY) ── só o TERMINAL do admin (A/R) vence; 'S'/'' oscilam
  const serverRaw = String(doc?.status_contestacao ?? "")
    .trim()
    .toUpperCase();
  const server: Contestacao = (["S", "A", "R"].includes(serverRaw)
    ? serverRaw
    : "") as Contestacao; // valor fora de S/A/R vira '' (não-contestado)
  const marcaContest = snap.contestacaoMap[id];
  const terminalLocal = marcaContest?.terminal; // A/R já visto uma vez (sticky local)
  const temContestLocal = !!marcaContest || snap.filaContestarIds.has(id);
  let contestacao: Contestacao;
  if (server === "A" || server === "R")
    contestacao = server; // decisão terminal do admin vence
  else if (terminalLocal === "A" || terminalLocal === "R")
    contestacao = terminalLocal; // TERMINAL STICKY: o server oscila 'R'→'S'/'' entre réplicas —
  // a label não regride p/ "Em contestação" nem some depois de vista a decisão
  else if (temContestLocal)
    contestacao = "S"; // otimista — NÃO some na oscilação '' ↔ 'S' do servidor
  else contestacao = server; // '' ou 'S' do servidor
  const contestado = contestacao === "S" || contestacao === "A";

  const obrigatorioAssinar =
    !assinado && contestacao !== "S" && contestacao !== "A";
  const statusExibicao: StatusExibicao = assinado ? "ASSINADO" : "PENDENTE";
  // Admin RESPONDEU a contestação (terminal A/R) e o colaborador ainda NÃO viu — nem localmente
  // (vistas) nem no servidor (data_log_contestacao_colaborador_visto). Usado pela Home p/ abrir o
  // sheet de resposta uma única vez.
  const servidorVistoContest =
    String(doc?.data_log_contestacao_colaborador_visto ?? "").trim() !== "";
  const respostaContestacaoPendenteVista =
    (contestacao === "A" || contestacao === "R") &&
    !snap.vistas.has(id) &&
    !servidorVistoContest;

  return {
    id,
    ativo,
    visivel,
    assinadoServidor,
    assinadoLocalPendente,
    assinado,
    selo,
    contestacao,
    contestado,
    obrigatorioAssinar,
    statusExibicao,
    respostaContestacaoPendenteVista,
    arquivoUrl,
  };
}

/** Resolve a lista inteira (só os documentos VISÍVEIS ao colaborador). */
export function resolverLista(
  dados: any,
  snap: DocSnapshot,
  nowMs: number,
  sessaoConfirmada = true,
): DocEstado[] {
  const docs = (dados?.documentos ?? dados) as any[];
  if (!Array.isArray(docs)) return [];
  return docs
    .map((d) => resolverDoc(d, snap, nowMs, sessaoConfirmada))
    .filter((e) => e.visivel);
}
